import { Component, input, output, model, inject, signal } from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Monitor } from '../../models/monitor.model';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { Corporate } from '/app/corporates/models/corporate';
import { MonitorsService } from '../../services/monitors.service';

@Component({
  selector: 'app-monitors-admin-table',
  imports: [MatButtonModule, MatIconModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatCheckboxModule, MatSelectModule],
  templateUrl: './monitors-admin-table.component.html',
  styleUrls: ['./monitors-admin-table.component.css']
})
export class MonitorsAdminTableComponent {
  dataSource = model<Monitor[]>([]);
  dataCorporate = input<Corporate[]>([]);

  showAddForm = signal(false);
  monitorForm: FormGroup;
  mode: 'add' | 'edit' = 'add';
  editingIndex: number | null = null;
  private originalMonitor: Monitor | null = null;

  private fb = inject(FormBuilder);
  private monitorsService = inject(MonitorsService);

  constructor() {
    this.monitorForm = this.fb.group({
      id: [this.generateId()],
      name: ['', Validators.required],
      corporateId: [null],
      clientId: [''],
      description: [''],
      riskEvaluation: [false],
      ruleId: [''],
    });
  }

  trackByMonitorId(index: number, item: Monitor): string {
    return item?.id ?? index.toString();
  }

  toggleAddForm(): void {
    this.showAddForm.update(v => !v);
    if (this.showAddForm()) {
      this.monitorForm.reset({ id: this.generateId(), name: '', corporateId: null, clientId: '', description: '', riskEvaluation: false });
    }
  }

  startEdit(monitor: Monitor): void {
    this.mode = 'edit';
    this.editingIndex = this.dataSource().indexOf(monitor);
    this.originalMonitor = monitor;
    this.monitorForm.patchValue({
      id: monitor.id,
      name: monitor.name,
      corporateId: (monitor as any).corporateId ?? '',
      clientId: monitor.clientId ?? '',
      description: monitor.description ?? '',
      riskEvaluation: !!monitor.riskEvaluation,
      ruleId: monitor.ruleId ?? ''
    });
    this.showAddForm.set(true);
  }

  generateId(): string {
    return 'm-' + Math.random().toString(36).substring(2, 9);
  }

  getCorporateName(id: string): string {
    const corp = this.dataCorporate().find(c => c.id === id);
    return corp ? corp.name : '';
  }

  saveMonitor(): void {
    if (this.monitorForm.invalid) {
      console.warn('Formulario inválido');
      return;
    }
    const v = this.monitorForm.value;

    if (this.mode === 'edit') {
      const orig = this.originalMonitor;
      const updatedMonitor: Monitor = orig ? {
        ...orig,
        id: v.id,
        name: v.name,
        corporateId: v.corporateId ?? orig.corporateId ?? '',
        clientId: v.clientId ?? orig.clientId ?? '',
        description: v.description ?? orig.description ?? '',
        riskEvaluation: !!v.riskEvaluation,
        ruleId: v.ruleId ?? orig.ruleId ?? ''
      } : {
        id: v.id,
        name: v.name,
        corporateId: v.corporateId || '',
        clientId: v.clientId || '',
        description: v.description || '',
        eventColors: {},
        allowedUserIds: [],
        riskEvaluation: !!v.riskEvaluation,
        ruleId: v.ruleId || ''
      };

      this.monitorsService.updateMonitor(updatedMonitor).subscribe({
        next: () => {
          if (this.editingIndex !== null) {
            this.dataSource.update(ds => {
              const newDs = [...ds];
              newDs[this.editingIndex!] = updatedMonitor;
              return newDs;
            });
          }
          this.showAddForm.set(false);
          this.mode = 'add';
          this.editingIndex = null;
          this.originalMonitor = null;
        },
        error: (error) => {
          console.error('Error updating monitor:', error);
        }
      });
    } else {
      const payload = {
        name: v.name,
        description: v.description || '',
        ruleId: v.ruleId || '',
        clientId: v.clientId || '',
        eventColors: {},
        allowedUserIds: [],
        corporateId: v.corporateId || '',
        riskEvaluation: !!v.riskEvaluation
      };

      this.monitorsService.setMonitors(payload).subscribe({
        next: (response) => {
          const newMon: Monitor = {
            id: (response as any).id || v.id || this.generateId(),
            name: v.name,
            corporateId: v.corporateId || '',
            clientId: v.clientId || '',
            description: v.description || '',
            eventColors: {},
            allowedUserIds: [],
            riskEvaluation: !!v.riskEvaluation,
            ruleId: v.ruleId || ''
          };
          this.dataSource.update(ds => [...ds, newMon]);
          this.showAddForm.set(false);
        },
        error: (error) => {
          console.error('Error creating monitor:', error);
        }
      });
    }
  }

  cancelAdd(): void {
    this.showAddForm.set(false);
    this.mode = 'add';
    this.editingIndex = null;
  }

  copyId(id: string | undefined | null): void {
    if (!id) return;
    const text = String(id);
    if (navigator && (navigator as any).clipboard && (navigator as any).clipboard.writeText) {
      (navigator as any).clipboard.writeText(text).then(() => {
      }).catch((err: any) => {
        console.error('Error copiando ID:', err);
      });
      return;
    }

    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    } catch (e) {
      console.error('No se pudo copiar el ID:', e);
    }
  }

  createRule(id: string, clientId: string): void {
  }
}
